import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { UserPlus, Trash2, ShieldCheck } from "lucide-react";

const TIERS = [
  { value: "team", label: "Team" },
  { value: "pro", label: "Pro" },
  { value: "free", label: "Free (revoke)" },
];

export function AccessManager() {
  const [grants, setGrants] = useState([]);
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("team");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try { setGrants(await api.get("/access/grants").then((r) => r.data)); }
    catch (_) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const grant = async () => {
    const e = email.trim().toLowerCase();
    if (!e.includes("@")) { setMsg("Enter a valid email."); return; }
    setBusy(true);
    setMsg("");
    try {
      const res = await api.post("/access/grant", { email: e, tier }).then((r) => r.data);
      setEmail("");
      setMsg(tier === "free"
        ? `Revoked access for ${e}.`
        : res.applied
          ? `${e} is now on ${tier.toUpperCase()}.`
          : `${e} will get ${tier.toUpperCase()} as soon as they sign up.`);
      await load();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Could not update access.");
    } finally { setBusy(false); }
  };

  const revoke = async (e) => {
    setBusy(true);
    try { await api.delete(`/access/grant/${encodeURIComponent(e)}`); await load(); }
    finally { setBusy(false); }
  };

  return (
    <section className="hud-frame p-6 space-y-5" data-testid="settings-access">
      <div className="flex items-center gap-2">
        <ShieldCheck size={14} className="text-[hsl(var(--oxblood))]" />
        <div className="kpi-label">OWNER · GRANT ACCESS</div>
      </div>
      <p className="mono text-[11px] text-muted-foreground tracking-widest uppercase">
        Give anyone a tier by email — works even before they sign up.
      </p>

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && grant()}
          placeholder="person@email.com"
          className="hud-input flex-1"
          data-testid="access-email"
        />
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          className="hud-input sm:w-40"
          data-testid="access-tier"
        >
          {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <button
          onClick={grant}
          disabled={busy}
          className="hud-btn hud-btn-primary flex items-center justify-center gap-2"
          data-testid="access-grant"
        >
          <UserPlus size={12} /> {busy ? "SAVING…" : "GRANT"}
        </button>
      </div>

      {msg && <div className="mono text-[11px] text-[hsl(var(--oxblood))] tracking-widest uppercase" data-testid="access-msg">{msg}</div>}

      {grants.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-border" data-testid="access-list">
          {grants.map((g) => (
            <div key={g.email} className="flex items-center justify-between gap-3" data-testid={`access-row-${g.email}`}>
              <div className="text-sm min-w-0">
                <span className="truncate">{g.email}</span>
                <span className="mono text-[10px] text-muted-foreground tracking-widest uppercase ml-2">
                  {g.tier.toUpperCase()} · {g.registered ? `active (${(g.current_plan || "").toUpperCase()})` : "pending signup"}
                </span>
              </div>
              <button
                onClick={() => revoke(g.email)}
                disabled={busy}
                className="text-muted-foreground hover:text-[hsl(var(--signal))]"
                title="Revoke access"
                data-testid={`access-revoke-${g.email}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
